import { Client as ColyseusClient } from 'colyseus.js';
import { isDuplicateSessionKick } from '../contracts/wsClose';
import { getColyseusWsUrl } from './colyseusUrl';
import {
  LOBBY_CLOSE_CONSENTED,
  readPersistedLobbyReconnect,
  writePersistedLobbyReconnect,
  clearPersistedLobbyReconnect,
} from './lobbyReconnectUtils';

const COMMANDS = {
  LIST_TABLES: 'lobby/list-tables',
  CREATE_TABLE: 'lobby/create-table',
  JOIN_TABLE: 'lobby/join-table',
  LEAVE_TABLE: 'lobby/leave-table',
  SET_READY: 'lobby/set-ready',
  KICK_FROM_TABLE: 'lobby/kick-from-table',
  TABLE_WAITING_CHAT: 'lobby/table-waiting-chat',
  FRIEND_REQUEST_NOTIFY: 'lobby/friend-request-notify',
  FRIEND_REQUEST_RESOLVE: 'lobby/friend-request-resolve',
  GLOBAL_LOBBY_CHAT: 'lobby/global-lobby-chat',
  SEND_INVITE: 'social/send-invite',
  RESOLVE_INVITE: 'social/resolve-invite',
};

const EVENTS = {
  ERROR: 'system/error',
  LOBBY_STATE: 'lobby/state',
  TABLE_CREATED: 'lobby/table-created',
  TABLE_UPDATED: 'lobby/table-updated',
  TABLE_REMOVED: 'lobby/table-removed',
  /** Синхронно с `server/src/contracts/messages.js` */
  TABLE_WAITING_CHAT: 'lobby/table-waiting-chat-msg',
  LOBBY_FRIEND_REQUEST: 'lobby/friend-request',
  LOBBY_FRIEND_REQUEST_RESOLVED: 'lobby/friend-request-resolved',
  LOBBY_GLOBAL_LOBBY_CHAT: 'lobby/global-lobby-chat-msg',
  SOCIAL_INVITE: 'social/invite',
};
const JOIN_ATTEMPT_TIMEOUT_MS = 7000;
const INVITE_ONLY_JOIN_ATTEMPT_TIMEOUT_MS = 15000;
const DEFAULT_GUEST_AVATAR_URL = `${process.env.PUBLIC_URL || ''}/guest-avatar.png`;
const DEFAULT_BELOT_VARIANT = 'armenian';

function createEnvelope(command, data) {
  return {
    command,
    meta: {
      commandId: `cmd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      clientVersion: `web-${process.env.REACT_APP_VERSION || 'dev'}`,
    },
    data: data || {},
  };
}

function normalizePlayerProfiles(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!v || typeof v !== 'object') continue;
    const avatarUrl = String(v.avatarUrl || '').trim() || DEFAULT_GUEST_AVATAR_URL;
    out[String(k)] = {
      username: String(v.username || k),
      level: Math.max(1, Math.floor(Number(v.level) || 1)),
      avatarUrl,
    };
  }
  return out;
}

function toRoomEntry(table) {
  const code = String(table?.tableId || table?.id || '').toUpperCase();
  const mode = String(table?.mode || 'seka');
  const order = Array.isArray(table?.playerOrder) ? table.playerOrder.map((x) => String(x)) : [];
  const readyRaw = Array.isArray(table?.readyUserIds) ? table.readyUserIds : [];
  const readyUserIds = readyRaw.map((x) => String(x)).filter((id) => order.includes(id));
  return {
    roomCode: code,
    name: String(table?.name || '').trim(),
    stake: Number(table?.buyIn || 100),
    ante: Math.max(0, Math.floor(Number(table?.ante || 0))),
    currency: String(table?.currency || 'seka'),
    belotVariant:
      mode === 'belot'
        ? String(table?.belotVariant || '').trim().toLowerCase() || DEFAULT_BELOT_VARIANT
        : '',
    belotTargetScore:
      mode === 'belot'
        ? Number(table?.belotTargetScore) === 5010 || Number(table?.belotTargetScore) === 501
          ? 5010
          : 10010
        : undefined,
    maxPlayers: Math.max(2, Math.min(9, Number(table?.maxPlayers || 2))),
    seated: order.length,
    players: order,
    isPrivate: Boolean(table?.isPrivate),
    hostUserId: String(table?.hostUserId || ''),
    readyUserIds,
    playerProfiles: normalizePlayerProfiles(table?.playerProfiles),
  };
}

class ColyseusOnlineLobbyTransport {
  /**
   * @param {import('colyseus.js').Room} room
   * @param {import('colyseus.js').Client} colyseusClient
   * @param {object} user
   * @param {{ accessToken?: string, mode: string }} joinArgs
   */
  constructor(room, colyseusClient, user, joinArgs) {
    this.room = room;
    this.client = colyseusClient;
    this.user = user;
    this.joinArgs = joinArgs;
    this.listeners = new Map();
    this.connected = true;
    this.tables = new Map();
    this.pendingCreateCode = null;
    this.pendingJoinCode = null;
    this._pendingJoinInviteOnly = false;
    /** Не считать «исключением» первые снимки до появления нас в playerOrder (гонка JOIN vs LOBBY_STATE). */
    this._pendingJoinSeatConfirmed = false;
    this._pendingJoinAttemptAt = 0;
    this._intentionalDisconnect = false;
    this._reconnectAttempt = 0;
    this._leaveHandler = (code, reason) => {
      void this._handleRoomLeave(code, reason);
    };
    this.room.onLeave(this._leaveHandler);
    this._wireInbound();
    writePersistedLobbyReconnect(joinArgs.mode, room);
    queueMicrotask(() => this._emitLocal('connect', { id: room.sessionId }));
  }

  _clearPendingJoinTracking() {
    this.pendingJoinCode = null;
    this.pendingCreateCode = null;
    this._pendingJoinSeatConfirmed = false;
    this._pendingJoinAttemptAt = 0;
    this._pendingJoinInviteOnly = false;
  }

  _markPendingJoin(code, options = {}) {
    this.pendingJoinCode = String(code || '').trim().toUpperCase();
    this.pendingCreateCode = null;
    this._pendingJoinSeatConfirmed = false;
    this._pendingJoinAttemptAt = Date.now();
    this._pendingJoinInviteOnly = Boolean(options?.inviteOnly);
  }

  /**
   * @param {object} user
   * @param {string} [accessToken]
   * @param {string} [mode] — filterBy на сервере (`seka` | `poker` | `belot`)
   */
  static async create(user, accessToken, mode = 'seka') {
    const client = new ColyseusClient(getColyseusWsUrl());
    if (accessToken) {
      client.http.authToken = String(accessToken);
    }
    const joinArgs = {
      mode: String(mode || 'seka'),
      accessToken,
    };
    const joinOpts = {
      mode: joinArgs.mode,
      userId: String(user?.id || 'guest'),
      username: String(user?.username || 'Игрок'),
      clan: String(user?.clan || ''),
      level: Math.max(1, Math.floor(Number(user?.level) || 1)),
      avatarUrl: String(user?.avatarUrl || DEFAULT_GUEST_AVATAR_URL).trim().slice(0, 12000),
    };

    const persisted = readPersistedLobbyReconnect(joinArgs.mode);
    let room = null;
    if (persisted) {
      try {
        room = await client.reconnect(persisted);
      } catch {
        room = null;
      }
    }
    if (!room) {
      room = await client.joinOrCreate('lobby', joinOpts);
    }
    return new ColyseusOnlineLobbyTransport(room, client, user, joinArgs);
  }

  _joinOptsForFallback() {
    const u = this.user || {};
    return {
      mode: this.joinArgs.mode,
      userId: String(u?.id || 'guest'),
      username: String(u?.username || 'Игрок'),
      clan: String(u?.clan || ''),
      level: Math.max(1, Math.floor(Number(u?.level) || 1)),
      avatarUrl: String(u?.avatarUrl || DEFAULT_GUEST_AVATAR_URL).trim().slice(0, 12000),
    };
  }

  async _handleRoomLeave(code, reason) {
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* room destroyed */
    }

    const token = this.room?.reconnectionToken;

    if (this._intentionalDisconnect) {
      this.connected = false;
      clearPersistedLobbyReconnect(this.joinArgs.mode);
      this._emitLocal('disconnect', { code, reason, intentional: true });
      return;
    }

    if (isDuplicateSessionKick(code, reason)) {
      this.connected = false;
      clearPersistedLobbyReconnect(this.joinArgs.mode);
      this._emitLocal('disconnect', { code, reason, duplicateSession: true });
      return;
    }

    const maxAttempts = 6;
    if (token && code !== LOBBY_CLOSE_CONSENTED && this._reconnectAttempt < maxAttempts) {
      this._reconnectAttempt += 1;
      this._emitLocal('reconnecting', { code, reason, attempt: this._reconnectAttempt, phase: 'token' });
      const delayMs = Math.min(4000, 200 * this._reconnectAttempt ** 2);
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const newRoom = await this.client.reconnect(token);
        this.room = newRoom;
        this.connected = true;
        this._reconnectAttempt = 0;
        newRoom.onLeave(this._leaveHandler);
        this._wireInbound();
        writePersistedLobbyReconnect(this.joinArgs.mode, newRoom);
        this._emitLocal('reconnected', { code, reason });
        return;
      } catch (e) {
        console.warn('[online-lobby] reconnect failed, trying full join', e);
      }
    }

    this._emitLocal('reconnecting', { code, reason, phase: 'full_join' });
    try {
      const newRoom = await this.client.joinOrCreate('lobby', this._joinOptsForFallback());
      this.room = newRoom;
      this.connected = true;
      this._reconnectAttempt = 0;
      newRoom.onLeave(this._leaveHandler);
      this._wireInbound();
      writePersistedLobbyReconnect(this.joinArgs.mode, newRoom);
      this._emitLocal('reconnected', { code, reason, fallback: 'join' });
    } catch (e) {
      console.error('[online-lobby] full rejoin failed', e);
      this.connected = false;
      clearPersistedLobbyReconnect(this.joinArgs.mode);
      this._emitLocal('disconnect', { code, reason, error: e });
    }
  }

  _wireInbound() {
    this.room.onMessage(EVENTS.ERROR, (payload) => {
      const code = String(payload?.code || '');
      if (code === 'TABLE_NOT_FOUND') {
        const failedCode = String(this.pendingJoinCode || '').trim().toUpperCase();
        const inviteOnly = Boolean(this._pendingJoinInviteOnly);
        this._clearPendingJoinTracking();
        this._emitLocal('JOIN_ERROR', {
          code,
          message: payload?.message || 'Комната не найдена',
          roomCode: failedCode,
          inviteOnly,
        });
      } else if (code === 'TABLE_BAD_PASSWORD') {
        const failedCode = String(this.pendingJoinCode || '').trim().toUpperCase();
        const inviteOnly = Boolean(this._pendingJoinInviteOnly);
        this._clearPendingJoinTracking();
        this._emitLocal('JOIN_ERROR', {
          code,
          message: payload?.message || 'Неверный пароль комнаты',
          roomCode: failedCode,
          inviteOnly,
        });
      } else if (code === 'INVITE_ACCESS_DENIED') {
        const failedCode = String(this.pendingJoinCode || '').trim().toUpperCase();
        const inviteOnly = Boolean(this._pendingJoinInviteOnly);
        this._clearPendingJoinTracking();
        this._emitLocal('JOIN_ERROR', {
          code,
          message: payload?.message || 'Комната доступна только приглашенному игроку.',
          roomCode: failedCode,
          inviteOnly,
        });
      } else {
        this._emitLocal('error', payload || { message: 'Colyseus error' });
      }
    });

    this.room.onMessage(EVENTS.LOBBY_STATE, (payload) => {
      const next = Array.isArray(payload?.tables) ? payload.tables : [];
      this.tables = new Map(next.map((t) => [String(t.tableId || t.id).toUpperCase(), t]));
      const rooms = next.map(toRoomEntry);
      this._emitLocal('ROOM_LIST_UPDATE', rooms);
      this._checkWaitingStillSeated();
      this._maybeEmitCreateOrStart();
    });

    this.room.onMessage(EVENTS.TABLE_CREATED, (payload) => {
      const code = String(payload?.tableId || '').toUpperCase();
      if (code) {
        const existing = this.tables.get(code) || {};
        this.tables.set(code, { ...existing, ...payload, tableId: code });
      }
      if (this.pendingCreateCode && this.pendingCreateCode === code) {
        this._emitLocal('ROOM_CREATED', { roomCode: code });
      }
      this._emitLocal('ROOM_LIST_UPDATE', Array.from(this.tables.values()).map(toRoomEntry));
      this._checkWaitingStillSeated();
      this._maybeEmitCreateOrStart();
    });

    this.room.onMessage(EVENTS.TABLE_UPDATED, (payload) => {
      const code = String(payload?.tableId || '').toUpperCase();
      const existing = this.tables.get(code) || {};
      this.tables.set(code, { ...existing, ...payload, tableId: code });
      this._emitLocal('ROOM_LIST_UPDATE', Array.from(this.tables.values()).map(toRoomEntry));
      this._checkWaitingStillSeated();
      this._maybeEmitCreateOrStart();
    });

    this.room.onMessage(EVENTS.TABLE_REMOVED, (payload) => {
      const code = String(payload?.tableId || '').toUpperCase();
      if (code) this.tables.delete(code);
      this._emitLocal('ROOM_LIST_UPDATE', Array.from(this.tables.values()).map(toRoomEntry));
      this._checkWaitingStillSeated();
      this._maybeEmitCreateOrStart();
    });

    this.room.onMessage(EVENTS.TABLE_WAITING_CHAT, (payload) => {
      this._emitLocal('TABLE_WAITING_CHAT', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_FRIEND_REQUEST, (payload) => {
      this._emitLocal('lobby_friend_request', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_FRIEND_REQUEST_RESOLVED, (payload) => {
      this._emitLocal('lobby_friend_request_resolved', payload || {});
    });

    this.room.onMessage(EVENTS.LOBBY_GLOBAL_LOBBY_CHAT, (payload) => {
      this._emitLocal('lobby_global_lobby_chat', payload || {});
    });
    this.room.onMessage(EVENTS.SOCIAL_INVITE, (payload) => {
      this._emitLocal('social_invite', payload || {});
    });
  }

  _checkWaitingStillSeated() {
    if (!this.pendingJoinCode) return;
    const uid = String(this.user?.id || '');
    const code = this.pendingJoinCode;
    const table = this.tables.get(code);
    if (!table) {
      if (!this._pendingJoinSeatConfirmed) {
        const startedAt = Number(this._pendingJoinAttemptAt || 0);
        const timeoutMs = this._pendingJoinInviteOnly
          ? INVITE_ONLY_JOIN_ATTEMPT_TIMEOUT_MS
          : JOIN_ATTEMPT_TIMEOUT_MS;
        if (startedAt && Date.now() - startedAt >= timeoutMs) {
          const inviteOnly = Boolean(this._pendingJoinInviteOnly);
          this._clearPendingJoinTracking();
          this._emitLocal('JOIN_ERROR', {
            code: 'JOIN_TIMEOUT',
            message: 'Не удалось подтвердить вход в комнату. Повторите подключение.',
            roomCode: code,
            inviteOnly,
          });
        }
        return;
      }
      this._clearPendingJoinTracking();
      this._emitLocal('WAITING_ROOM_LOST', { roomCode: code, reason: 'table_removed' });
      return;
    }
    const order = Array.isArray(table.playerOrder) ? table.playerOrder.map((x) => String(x)) : [];
    if (uid && order.includes(uid)) {
      this._pendingJoinSeatConfirmed = true;
      return;
    }
    if (uid && !order.includes(uid)) {
      if (!this._pendingJoinSeatConfirmed) return;
      this._clearPendingJoinTracking();
      this._emitLocal('WAITING_ROOM_LOST', { roomCode: code, reason: 'not_seated' });
    }
  }

  _maybeEmitCreateOrStart() {
    if (this.pendingCreateCode) {
      const table = this.tables.get(this.pendingCreateCode);
      if (table) {
        this._emitLocal('ROOM_CREATED', { roomCode: this.pendingCreateCode });
      }
    }

    if (!this.pendingJoinCode) return;
    const table = this.tables.get(this.pendingJoinCode);
    if (!table) return;
    const order = Array.isArray(table.playerOrder) ? table.playerOrder.map((x) => String(x)) : [];
    const maxPlayers = Math.max(2, Math.min(9, Number(table.maxPlayers || 2)));
    if (order.length < maxPlayers) return;
    const readyList = Array.isArray(table.readyUserIds) ? table.readyUserIds : [];
    const ready = new Set(readyList.map((id) => String(id)));
    const allReady = order.length > 0 && order.every((id) => ready.has(id));
    if (!allReady) return;
    const userId = String(this.user?.id || '');
    const playerIndexRaw = order.indexOf(userId);
    const playerIndex = playerIndexRaw >= 0 ? playerIndexRaw : 0;
    const gid = String(table.gameRoomId || '').trim();
    /** Пустой gid: OnlineLobby игнорирует GAME_START, но раньше pendingJoinCode всё равно сбрасывался — второй клиент терял старт. */
    if (!gid) return;
    this._emitLocal('GAME_START', {
      roomCode: this.pendingJoinCode,
      gameRoomId: gid,
      players: order.map((id) => ({ id, username: id })),
      playerIndex,
      gameState: {
        mode: table.mode || 'seka',
        roomCode: this.pendingJoinCode,
        gameRoomId: gid,
      },
    });
    this.pendingJoinCode = null;
    this.pendingCreateCode = null;
    this._pendingJoinSeatConfirmed = false;
    this._pendingJoinInviteOnly = false;
  }

  _emitLocal(eventName, payload) {
    const subs = this.listeners.get(eventName);
    if (!subs) return;
    subs.forEach((cb) => {
      try {
        cb(payload);
      } catch (e) {
        console.error('[online-lobby-colyseus] listener failed', e);
      }
    });
  }

  on(eventName, cb) {
    if (!this.listeners.has(eventName)) this.listeners.set(eventName, new Set());
    this.listeners.get(eventName).add(cb);
  }

  off(eventName, cb) {
    if (!this.listeners.has(eventName)) return;
    if (!cb) {
      this.listeners.delete(eventName);
      return;
    }
    this.listeners.get(eventName).delete(cb);
  }

  emit(eventName, payload) {
    switch (eventName) {
      case 'GET_ROOM_LIST':
        this.room.send('command', createEnvelope(COMMANDS.LIST_TABLES, {}));
        return;
      case 'CREATE_ROOM': {
        const baseCode = String(
          payload?.roomCode || `R${Math.random().toString(36).slice(2, 7).toUpperCase()}`
        ).toUpperCase();
        this.pendingCreateCode = baseCode;
        /** Как у JOIN_ROOM: GAME_START срабатывает только при непустом `pendingJoinCode`. */
        this.pendingJoinCode = baseCode;
        this._pendingJoinSeatConfirmed = false;
        const mode = payload?.mode || 'seka';
        const rawMax = Number(payload?.maxPlayers ?? 2);
        const maxPlayers = Math.max(2, Math.min(9, Number.isFinite(rawMax) ? rawMax : 2));
        const buyIn = Number(payload?.buyIn ?? payload?.teamStake ?? 100);
        const currency = String(payload?.currency || 'seka');
        const nameTrim = String(payload?.name || '').trim();
        const name = nameTrim || `${mode}-${baseCode}`;
        const anteRaw = Number(payload?.ante);
        const createPayload = {
          tableId: baseCode,
          mode,
          buyIn: Number.isFinite(buyIn) ? buyIn : 100,
          currency,
          maxPlayers,
          name,
        };
        if (mode === 'belot') {
          createPayload.belotVariant = String(payload?.belotVariant || DEFAULT_BELOT_VARIANT)
            .trim()
            .toLowerCase();
          const ts = Number(payload?.belotTargetScore);
          createPayload.belotTargetScore = ts === 5010 || ts === 501 ? 5010 : 10010;
          createPayload.belotShulerEnabled = Boolean(
            payload?.belotShulerEnabled &&
              maxPlayers === 4 &&
              createPayload.belotVariant !== 'open_duell',
          );
        }
        if (mode === 'seka' && Number.isFinite(anteRaw) && anteRaw > 0) {
          createPayload.ante = Math.floor(anteRaw);
        }
        if (payload?.isPrivate) {
          createPayload.isPrivate = true;
          const pw = String(payload?.roomPassword || '').trim();
          if (pw) createPayload.roomPassword = pw;
        }
        this.room.send('command', createEnvelope(COMMANDS.CREATE_TABLE, createPayload));
        return;
      }
      case 'JOIN_ROOM': {
        const code = String(payload?.roomCode || '').trim().toUpperCase();
        this._markPendingJoin(code, { inviteOnly: Boolean(payload?.inviteOnly) });
        const joinData = { tableId: code };
        const jpw = String(payload?.roomPassword || '').trim();
        if (jpw) joinData.roomPassword = jpw;
        this.room.send('command', createEnvelope(COMMANDS.JOIN_TABLE, joinData));
        return;
      }
      case 'LEAVE_TABLE': {
        const tableId = String(payload?.tableId || '').trim().toUpperCase();
        if (!tableId) return;
        if (this.pendingJoinCode === tableId) {
          this._clearPendingJoinTracking();
        }
        this.room.send('command', createEnvelope(COMMANDS.LEAVE_TABLE, { tableId }));
        return;
      }
      case 'SET_TABLE_READY': {
        const tableId = String(payload?.tableId || '').trim().toUpperCase();
        if (!tableId) return;
        this.room.send(
          'command',
          createEnvelope(COMMANDS.SET_READY, { tableId, ready: Boolean(payload?.ready) })
        );
        return;
      }
      case 'KICK_FROM_TABLE': {
        const tableId = String(payload?.tableId || '').trim().toUpperCase();
        const targetUserId = String(payload?.targetUserId || '');
        if (!tableId || !targetUserId) return;
        this.room.send(
          'command',
          createEnvelope(COMMANDS.KICK_FROM_TABLE, { tableId, targetUserId })
        );
        return;
      }
      case 'TABLE_WAITING_CHAT': {
        const tableId = String(payload?.tableId || '').trim().toUpperCase();
        const text = String(payload?.text || '').trim();
        if (!tableId || !text) return;
        this.room.send(
          'command',
          createEnvelope(COMMANDS.TABLE_WAITING_CHAT, { tableId, text: text.slice(0, 280) })
        );
        return;
      }
      case 'FRIEND_REQUEST_NOTIFY': {
        const targetUserId = String(payload?.targetUserId || '').trim();
        if (!targetUserId) return;
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_REQUEST_NOTIFY, { targetUserId }));
        return;
      }
      case 'FRIEND_REQUEST_RESOLVE': {
        const otherUserId = String(payload?.otherUserId || '').trim();
        const status = String(payload?.status || '').toLowerCase();
        if (!otherUserId || (status !== 'accepted' && status !== 'declined')) return;
        this.room.send('command', createEnvelope(COMMANDS.FRIEND_REQUEST_RESOLVE, { otherUserId, status }));
        return;
      }
      case 'GLOBAL_LOBBY_CHAT': {
        const text = String(payload?.text || '').trim().slice(0, 220);
        if (!text) return;
        this.room.send('command', createEnvelope(COMMANDS.GLOBAL_LOBBY_CHAT, { text }));
        return;
      }
      case 'SOCIAL_SEND_INVITE': {
        const targetUserId = String(payload?.targetUserId || payload?.toUserId || '').trim();
        if (!targetUserId) return;
        this.room.send(
          'command',
          createEnvelope(COMMANDS.SEND_INVITE, {
            targetUserId,
            mode: String(payload?.mode || 'seka'),
            belotVariant: String(payload?.belotVariant || '').trim().toLowerCase(),
            stake: Math.max(1, Math.floor(Number(payload?.stake) || 0)),
            ante: Math.max(0, Math.floor(Number(payload?.ante) || 0)),
            roomCode: String(payload?.roomCode || payload?.tableId || '').trim().toUpperCase(),
            gameRoomId: String(payload?.gameRoomId || '').trim(),
          })
        );
        return;
      }
      case 'SOCIAL_RESOLVE_INVITE': {
        const inviteId = String(payload?.inviteId || '').trim();
        const decision = String(payload?.decision || '').trim().toLowerCase();
        if (!inviteId || !decision) return;
        this.room.send('command', createEnvelope(COMMANDS.RESOLVE_INVITE, { inviteId, decision }));
        return;
      }
      default:
        console.warn(`[online-lobby-colyseus] unsupported emit ${eventName}`);
    }
  }

  connect() {
    this.connected = true;
    this._emitLocal('connect', { id: this.room.sessionId });
  }

  disconnect() {
    this._intentionalDisconnect = true;
    this.connected = false;
    try {
      this.room.onLeave.remove(this._leaveHandler);
    } catch {
      /* ignore */
    }
    clearPersistedLobbyReconnect(this.joinArgs.mode);
    this.room.leave(true);
    this.listeners.clear();
  }
}

async function createOnlineLobbyTransport(user, accessToken, mode) {
  return ColyseusOnlineLobbyTransport.create(user, accessToken, mode);
}

export { createOnlineLobbyTransport, ColyseusOnlineLobbyTransport };
